/* eslint-disable ember/no-computed-properties-in-native-classes */
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import { isProductDisabled } from 'garaje/helpers/product-disabled';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { GLOBAL_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

// Checks if a list of roles contains any of the permitted roles
function includesAny(collection: Array<string>, ...values: Array<string>): boolean {
  return isPresent(_intersection(collection, values));
}

export default class DeskAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare featureConfig: FeatureConfigService;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;

  @reads('state.features') features!: StateService['features'];
  @reads('currentAdmin.roleNames') roles!: CurrentAdminService['roleNames'];

  get canManage(): boolean {
    return this.hasDesksAccess && includesAny(this.roles, GLOBAL_ADMIN);
  }

  get canUsePartialDayBooking(): boolean {
    return (
      Boolean(this.features?.canUsePartialDayBooking) &&
      this.featureFlags.isEnabled('partial-day-booking-web') &&
      this.featureConfig.isEnabled('deskReservations.partialDayBooking') &&
      Boolean(this.state.currentLocationEnabledDesks)
    );
  }

  // Admin can see the desk column in the entry log
  get canSeeEntryLog(): boolean {
    const permissions = [Permission.MAPS_FLOOR_READ, Permission.DESKS_DESK_READ, Permission.DESKS_AMENITY_READ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // Admin can see the desk column in the invite log
  get canSeeInviteLog(): boolean {
    const permissions = [Permission.MAPS_FLOOR_READ, Permission.DESKS_DESK_READ, Permission.DESKS_AMENITY_READ];

    return this.hasDesksAccess && this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // NAVIGATION

  /**
   * Whether the user has desks access via subscription features or the app_desks flag.
   * This is the single source of truth — routes check via abilities.can('access desk').
   */
  get hasDesksAccess(): boolean {
    return this.featureConfig.isEnabled('desks') || !!this.features?.canAccessDesksApplication;
  }

  /**
   * Exposed as can('access desk') for route guards.
   */
  get canAccess(): boolean {
    return this.hasDesksAccess;
  }

  get canVisit(): boolean {
    // cannot visit if product is disabled
    if (isProductDisabled('desks')) return false;

    // Under new pricing, hide Desks unless the account has an active desks product —
    // either a reservations bundle (new) or a standalone desks subscription (legacy).
    // Use isUsableWithBasic so legacy subscriptions with plan "basic" are not excluded.
    if (this.featureFlags.isEnabled('wrkx-pricing-packaging')) {
      const hasActiveDesksProduct =
        this.state.reservationsSubscription?.isUsable || this.state.desksSubscription?.isUsableWithBasic;
      if (!hasActiveDesksProduct) return false;
    }

    if (this.canVisitAllDesks) return true;
    if (this.canVisitSettings) return true;
    if (this.canVisitReservations) return true;
    if (this.canVisitLanding) return true;
    if (this.canVisitGetStarted) return true;

    return false;
  }

  get canVisitAllDesks(): boolean {
    if (!this.hasDesksAccess) return false;

    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.DESKS_DESK_READ,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_AMENITY_UPDATE,
      Permission.DESKS_AMENITY_DELETE,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canVisitReservations(): boolean {
    if (!this.hasDesksAccess) return false;
    if (!this.featureConfig.isEnabled('deskReservations')) return false;

    const permissions = [Permission.DESKS_RESERVATION_READ, Permission.MAPS_FLOOR_READ, Permission.DESKS_DESK_READ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canVisitSettings(): boolean {
    if (!this.hasDesksAccess) return false;

    const permissions = [
      Permission.DESKS_DESK_SETTING_CREATE,
      Permission.DESKS_DESK_SETTING_UPDATE,
      Permission.DESKS_DESK_SETTING_DELETE,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // No hasDesksAccess check — landing page is available for trial/upsell
  get canVisitLanding(): boolean {
    const permissions = [
      Permission.DESKS_DESK_CREATE,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_NEIGHBORHOOD_CREATE,
      Permission.MAPS_FLOOR_CREATE,
    ];

    if (!this.authz.hasAllPermissionsAtCurrentLocation(permissions)) return false;

    return this.canRequestDesksTrial || this.abilities.can('start-workplace-trial billing');
  }

  get canVisitGetStarted(): boolean {
    if (!this.hasDesksAccess) return false;

    const permissions = [
      Permission.DESKS_DESK_CREATE,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_NEIGHBORHOOD_CREATE,
      Permission.MAPS_FLOOR_CREATE,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // TRIALS

  get canRequestDesksTrial(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_TRIAL_REQUEST_CREATE);
  }

  // LIVE MAP

  get canShowBulkActions(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.MAPS_AREA_MAP_READ,
      Permission.MAPS_FEATURE_UPDATE,
      Permission.MAPS_FEATURE_DELETE,
      Permission.DESKS_DESK_UPDATE,
      Permission.DESKS_DESK_DELETE,
    ];

    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasAllPermissionsAtCurrentLocation(permissions)
      : true;
  }

  get canManageDesk(): boolean {
    const permissions = [
      Permission.MAPS_LIVE_CREATE,
      Permission.MAPS_LIVE_UPDATE,
      Permission.MAPS_LIVE_DELETE,
      Permission.MAPS_FEATURE_CREATE,
      Permission.MAPS_FEATURE_UPDATE,
      Permission.MAPS_FEATURE_DELETE,
      Permission.DESKS_DESK_CREATE,
      Permission.DESKS_DESK_UPDATE,
      Permission.DESKS_DESK_DELETE,
      Permission.DESKS_AMENITY_READ,
      Permission.DESKS_NEIGHBORHOOD_READ,
    ];

    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasAllPermissionsAtCurrentLocation(permissions)
      : true;
  }

  get canManagePointOfInterest(): boolean {
    const permissions = [
      Permission.MAPS_LIVE_UPDATE,
      Permission.MAPS_FEATURE_CREATE,
      Permission.MAPS_FEATURE_UPDATE,
      Permission.MAPS_FEATURE_DELETE,
    ];

    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasAllPermissionsAtCurrentLocation(permissions)
      : true;
  }

  // ALL DESKS

  get canViewFloors(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_READ);
  }

  get canViewFloorDetails(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.DESKS_NEIGHBORHOOD_READ,
      Permission.DESKS_DESK_READ,
      Permission.DESKS_AMENITY_READ,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canManageFloor(): boolean {
    return this.canCreateFloor && this.canEditFloor && this.canDeleteFloor;
  }

  get canCreateFloor(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_CREATE);
  }

  get canEditFloor(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_UPDATE);
  }

  get canDeleteFloor(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.MAPS_FLOOR_DELETE);
  }

  get canImportDesks(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_CREATE,
      Permission.MAPS_FLOOR_UPDATE,
      Permission.DESKS_DESK_IMPORT,
      Permission.DESKS_AMENITY_CREATE,
      Permission.DESKS_AMENITY_UPDATE,
      Permission.DESKS_NEIGHBORHOOD_CREATE,
      Permission.DESKS_NEIGHBORHOOD_UPDATE,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  get canExportDesks(): boolean {
    const permissions = [
      Permission.MAPS_FLOOR_READ,
      Permission.DESKS_DESK_EXPORT,
      Permission.DESKS_AMENITY_READ,
      Permission.DESKS_NEIGHBORHOOD_READ,
    ];

    return this.authz.hasAllPermissionsAtCurrentLocation(permissions);
  }

  // NEIGHBORHOODS

  get canCreateNeighborhood(): boolean {
    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_NEIGHBORHOOD_CREATE)
      : true;
  }

  get canEditNeighborhood(): boolean {
    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_NEIGHBORHOOD_UPDATE)
      : true;
  }

  get canDeleteNeighborhood(): boolean {
    return this.featureFlags.isEnabled('map-authz-spaces2')
      ? this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_NEIGHBORHOOD_DELETE)
      : true;
  }

  get canUseDeskLending(): boolean {
    if (!this.featureFlags.isEnabled('reverse-hoteling')) return false;
    if (!this.featureConfig.isEnabled('deskReservations.deskLending')) return false;

    return this.authz.hasPermissionAtCurrentLocation(Permission.DESKS_DESK_SETTING_UPDATE_DESK_LENDING);
  }

  get canLendDesk(): boolean {
    if (!this.featureFlags.isEnabled('reverse-hoteling')) return false;

    return this.featureConfig.isEnabled('deskReservations.deskLending');
  }
}
